import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type {
  ReadPostgridCheque,
  ReadPostgridLetter,
} from '@/services/api/generated/postgridService';
import {
  usePostgridChequesListQuery,
  usePostgridLettersListQuery,
} from '@/services/api/postgridService';

import { MailableListCard } from './MailableListCard';

export interface MailableListProps {
  matterId?: string;
  onDetailsClick?: (id: string, type: 'cheque' | 'letter') => void;
}

type MailableWithType = (ReadPostgridCheque | ReadPostgridLetter) & {
  _type: 'cheque' | 'letter';
};

// Type guard to check if the mailable is a cheque
const isCheque = (
  mailable: ReadPostgridCheque | ReadPostgridLetter,
): mailable is ReadPostgridCheque => {
  return 'amount' in mailable;
};

// Helper function to add type information and sort mailables
const combineAndSortMailables = (
  cheques: ReadPostgridCheque[],
  letters: ReadPostgridLetter[],
): MailableWithType[] => {
  const chequesWithType: MailableWithType[] = cheques.map((cheque) => ({
    ...cheque,
    _type: 'cheque' as const,
  }));

  const lettersWithType: MailableWithType[] = letters.map((letter) => ({
    ...letter,
    _type: 'letter' as const,
  }));

  const combined = [...chequesWithType, ...lettersWithType];

  // Sort by created_at (most recent first)
  return combined.sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
};

export const MailableList: React.FC<MailableListProps> = ({
  matterId,
  onDetailsClick,
}) => {
  const {
    data: cheques = [],
    isLoading: chequesLoading,
    error: chequesError,
  } = usePostgridChequesListQuery({ matterId });

  const {
    data: letters = [],
    isLoading: lettersLoading,
    error: lettersError,
  } = usePostgridLettersListQuery({ matterId });

  const isLoading = chequesLoading || lettersLoading;
  const hasError = chequesError || lettersError;

  const handleDetailsClick = (mailableId: string) => {
    // Find the mailable to determine its type
    const cheque = cheques.find((c) => c.id === mailableId);
    const letter = letters.find((l) => l.id === mailableId);

    if (cheque) {
      onDetailsClick?.(mailableId, 'cheque');
    } else if (letter) {
      onDetailsClick?.(mailableId, 'letter');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-sm text-gray-600">Loading mailables...</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertDescription>
          Failed to load mailables. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const mailables = combineAndSortMailables(cheques, letters);

  if (mailables.length === 0) {
    return (
      <div className="text-center p-8">
        <div className="text-gray-500 mb-2">No mailables found</div>
        <div className="text-sm text-gray-400">
          {matterId
            ? 'No cheques or letters have been created for this matter yet.'
            : 'No cheques or letters have been created yet.'}
        </div>
      </div>
    );
  }

  // Group mailables by test/live status for better organization
  const testMailables = mailables.filter((m) => m.is_test ?? !m.live);
  const liveMailables = mailables.filter((m) => !(m.is_test ?? !m.live));

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {mailables.length} mailables
          {matterId && ' for this matter'}
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {cheques.length > 0 && (
            <span>
              {cheques.length} cheque{cheques.length !== 1 ? 's' : ''}
            </span>
          )}
          {letters.length > 0 && (
            <span>
              {letters.length} letter{letters.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Live Mailables */}
      {liveMailables.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Live Mailables ({liveMailables.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {liveMailables.map((mailable) => (
              <MailableListCard
                key={mailable.id}
                mailable={mailable}
                onDetailsClick={handleDetailsClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Test Mailables */}
      {testMailables.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Test Mailables ({testMailables.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {testMailables.map((mailable) => (
              <MailableListCard
                key={mailable.id}
                mailable={mailable}
                onDetailsClick={handleDetailsClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
